import { getSpecialtiesRequest } from "@/store/reducers/specialtiesSlice";
import { selectSpecialties } from "@/store/selectors/specialtiesSelector";
import { formatDateForInput } from "@/utils/date.utils";
import { formatMoney } from "@/utils/format.utils";
import {
  formatPhoneNumberWithCountryCode,
  Modal,
} from "@ews-admin/global-design-system";
import { zodResolver } from "@hookform/resolvers/zod";
import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { doctorFormSchema, secretaryFormSchema } from "../../../schemas";
import { CreateDoctorPayload } from "../../../types/doctor.definition";
import { Specialty } from "../../../types/specialty.definition";
import { Staff } from "../../../types/staff.definition";
import {
  CivilityEnum,
  NotificationPrefEnum,
  RoleEnum,
  RoleType,
} from "../../../types/user.definition";
import { StaffSearchStep } from "../StaffSearchStep";
import { DoctorForm } from "./DoctorForm";
import { SecretaryForm } from "./SecretaryForm";

export interface StaffFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (formData: Partial<CreateDoctorPayload>) => void;
  editingStaff?: Staff | null;
  isSubmitting?: boolean;
  successMessage?: string | null;
  isDoctorLimitReached?: boolean;
  isSecretaryLimitReached?: boolean;
  currentDoctorCount?: number;
  maxDoctors?: number;
  currentSecretaryCount?: number;
  maxSecretaries?: number;
  paymentDueDate?: string | null;
  pricePerDoctor?: number;
  onUpgradeLicence?: () => void;
  facilityId?: number;
  onAssignSuccess?: () => void;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type StaffFormData = any;

export const StaffForm: React.FC<StaffFormProps> = ({
  isOpen,
  onClose,
  onSubmit,
  editingStaff = null,
  isSubmitting = false,
  successMessage = null,
  isDoctorLimitReached = false,
  isSecretaryLimitReached = false,
  currentDoctorCount = 0,
  maxDoctors = 0,
  currentSecretaryCount = 0,
  maxSecretaries = 0,
  paymentDueDate = null,
  pricePerDoctor = 20000,
  onUpgradeLicence,
  facilityId,
  onAssignSuccess,
}) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const specialties = useSelector(selectSpecialties);
  // Determine form type and schema
  const isEditMode = !!editingStaff;
  const [formStep, setFormStep] = useState<"search" | "create">(
    isEditMode ? "create" : "search",
  );

  // Default role: pick the available one when only one limit is reached
  const defaultRole = useMemo<RoleType>(() => {
    if (!isEditMode && isDoctorLimitReached && !isSecretaryLimitReached) {
      return RoleEnum.SECRETARY;
    }
    if (!isEditMode && isSecretaryLimitReached && !isDoctorLimitReached) {
      return RoleEnum.DOCTOR;
    }
    return RoleEnum.DOCTOR;
  }, [isEditMode, isDoctorLimitReached, isSecretaryLimitReached]);

  // State to track current roleName for dynamic schema
  const [currentRoleNameState, setCurrentRoleNameState] = useState<RoleType>(
    isEditMode ? (editingStaff?.roleName as RoleType) : defaultRole,
  );

  const formSchema = useMemo(() => {
    return currentRoleNameState === RoleEnum.SECRETARY
      ? secretaryFormSchema(t, isEditMode)
      : doctorFormSchema(t, isEditMode);
  }, [currentRoleNameState, t, isEditMode]);

  // Prepare default values
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const defaultValues = useMemo((): any => {
    if (editingStaff) {
      const commonFields = {
        firstName: editingStaff.firstName ?? "",
        lastName: editingStaff.lastName || "",
        email: editingStaff.contactInfo?.email || editingStaff.email || "",
        phoneNumber: formatPhoneNumberWithCountryCode(
          editingStaff.contactInfo?.phoneNumber ||
            editingStaff.phoneNumber ||
            "",
        ),
        civility: editingStaff.civility,
        roleName: editingStaff.roleName,
        photoUrl: editingStaff.photoUrl || "",
        activated:
          editingStaff.activated !== undefined ? editingStaff.activated : true,
        healthcareFacilityIds:
          editingStaff.healthcareFacilities?.map((f) => f.id) || [],
        preferredNotificationMethods:
          editingStaff.preferredNotificationMethods || [
            NotificationPrefEnum.EMAIL,
          ],
      };

      if (editingStaff.roleName === RoleEnum.DOCTOR) {
        return {
          ...commonFields,
          refNumber: editingStaff.refNumber || "",
          hospitalName:
            editingStaff.healthcareFacilities?.[0]?.name ||
            editingStaff.hospitalName ||
            "",
          careerStartYear:
            formatDateForInput(editingStaff.careerStartYear) || "",
          dateOfBirth: formatDateForInput(editingStaff.dateOfBirth) || "",
          specialties: editingStaff.specialties || [],
          providedServices: (editingStaff.providedServices || []).map(
            (service) => ({
              code: service.code,
              price: service.price,
            }),
          ),
        };
      } else {
        return {
          ...commonFields,
          healthcareFacilityId: editingStaff.healthcareFacility?.id || "",
          dateOfBirth: formatDateForInput(editingStaff.dateOfBirth) || "",
        };
      }
    } else {
      return {
        firstName: "",
        lastName: "",
        email: "",
        phoneNumber: "",
        providedServices: [],
        specialties: [],
        refNumber: "",
        hospitalName: "",
        careerStartYear: "",
        dateOfBirth: "",
        civility: CivilityEnum.MR,
        roleName: defaultRole,
        activated: true,
        healthcareFacilityIds: [1],
        preferredNotificationMethods: [NotificationPrefEnum.EMAIL],
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any;
    }
  }, [editingStaff, defaultRole]);

  // Initialize react-hook-form with dynamic schema
  // The formSchema will change when currentRoleNameState changes
  const {
    control,
    handleSubmit,
    formState: { errors, isValid, isDirty },
    setValue,
    watch,
    reset,
    trigger,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } = useForm<any>({
    resolver: zodResolver(formSchema),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    defaultValues: defaultValues as any,
    // MED-1653: validate on blur too, so a required field left empty (e.g. the
    // Reference number) shows its error instead of silently disabling submit.
    mode: "all",
    reValidateMode: "onBlur",
  });

  // Watch roleName and update schema state when it changes (for create mode)
  const watchedRoleName = watch("roleName") as RoleType;
  useEffect(() => {
    if (
      !isEditMode &&
      watchedRoleName &&
      watchedRoleName !== currentRoleNameState
    ) {
      setCurrentRoleNameState(watchedRoleName);
    }
  }, [watchedRoleName, isEditMode, currentRoleNameState]);

  // Track the editingStaff id to detect when a different staff is being edited
  const [lastEditingStaffId, setLastEditingStaffId] = useState<
    string | number | null
  >(null);

  // Reset form when editingStaff changes (different staff or switching between create/edit)
  useEffect(() => {
    const currentStaffId = editingStaff?.id || null;

    // Only reset if the staff being edited has changed
    if (currentStaffId !== lastEditingStaffId) {
      setLastEditingStaffId(currentStaffId);
      setFormStep(editingStaff ? "create" : "search");

      // Update currentRoleNameState when editingStaff changes
      if (editingStaff?.roleName) {
        setCurrentRoleNameState(editingStaff.roleName as RoleType);
      } else {
        setCurrentRoleNameState(defaultRole);
      }

      reset(defaultValues, {
        keepDefaultValues: false,
      });
      // Trigger validation after reset to update isValid
      setTimeout(() => {
        trigger();
      }, 0);
    }
  }, [
    editingStaff?.id,
    editingStaff?.roleName,
    defaultValues,
    lastEditingStaffId,
    reset,
    trigger,
    defaultRole,
    editingStaff,
  ]);

  // Reset search step when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormStep(editingStaff ? "create" : "search");
    }
  }, [isOpen, editingStaff]);

  // Fetch specialties from API
  useEffect(() => {
    if (isOpen && specialties.length === 0) {
      dispatch(getSpecialtiesRequest());
    }
  }, [isOpen, dispatch, specialties.length]);

  // Handle form submission
  const onSubmitForm = (data: Partial<StaffFormData>) => {
    onSubmit(data as Partial<CreateDoctorPayload>);
  };

  const formValues = watch();
  const currentRoleName = watchedRoleName;
  const isDoctor =
    isEditMode && editingStaff
      ? editingStaff.roleName === RoleEnum.DOCTOR
      : currentRoleName === RoleEnum.DOCTOR;

  const proRatedCost = useMemo(() => {
    if (!paymentDueDate || editingStaff) return null;
    const now = new Date();
    const due = new Date(paymentDueDate);
    const daysLeft = Math.ceil(
      (due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24),
    );
    if (daysLeft <= 0 || daysLeft >= 31) return null;
    const cost = Math.min(
      Math.ceil((pricePerDoctor / 30) * daysLeft * 1.03),
      pricePerDoctor,
    );
    return cost;
  }, [paymentDueDate, pricePerDoctor, editingStaff]);

  if (!isOpen) return null;

  const selectedSpecialties = (
    (formValues.specialties as Specialty[]) || []
  ).map((specialty: Specialty) => {
    const storeSpecialty = specialties.find(
      (s) => s.code === specialty.code || String(s.id) === String(specialty.id),
    );
    return {
      id: specialty.id as string,
      code: specialty.code,
      label: specialty.label || storeSpecialty?.label || specialty.code,
    };
  });
  return (
    <Modal
      isOpen
      onClose={onClose}
      title={
        editingStaff
          ? t("staff.manage.form.editStaff")
          : t("staff.manage.form.addStaff")
      }
      variant="info"
      size="4xl"
      contentClassName="max-h-[75vh] overflow-y-auto"
    >
      {/* Sub-header: step hint / back-to-search */}
      {!isEditMode && formStep === "search" && (
        <p className="mb-4 -mt-2 text-sm text-neutral-500">
          {t("staff.manage.search.label")}
        </p>
      )}
      {!isEditMode && formStep === "create" && (
        <button
          type="button"
          onClick={() => setFormStep("search")}
          className="mb-4 -mt-2 text-sm text-primary-600 hover:text-primary-700"
        >
          ← {t("staff.manage.search.backToSearch")}
        </button>
      )}

      {/* Success/Error Messages */}
      {successMessage && (
        <div className="p-4 mb-4 bg-green-50 rounded-md border border-green-200">
          <p className="text-sm font-medium text-green-800">{successMessage}</p>
        </div>
      )}
            {/* Search-first step (create mode only) */}
            {!isEditMode && formStep === "search" && facilityId && (
              <StaffSearchStep
                facilityId={facilityId}
                onAssignSuccess={onAssignSuccess}
                onCreateNew={() => setFormStep("create")}
                isDoctorLimitReached={isDoctorLimitReached}
                isSecretaryLimitReached={isSecretaryLimitReached}
                currentDoctorCount={currentDoctorCount}
                maxDoctors={maxDoctors}
                currentSecretaryCount={currentSecretaryCount}
                maxSecretaries={maxSecretaries}
                onUpgradeLicence={onUpgradeLicence}
              />
            )}

            {/* Creation / edit form */}
            {(isEditMode || formStep === "create") && (
              <>
                {/* Pro-rated addition cost — only shown when the facility has NO remaining
                    paid doctor slots (currentDoctorCount >= licence.numberOfDoctors, exposed as
                    isDoctorLimitReached). Any doctor added beyond the paid quota triggers a
                    pro-rated charge for the current billing period. */}
                {isDoctor &&
                  !editingStaff &&
                  isDoctorLimitReached &&
                  proRatedCost !== null && (
                    <div className="p-4 mb-4 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="text-sm font-medium text-blue-800">
                        {t("staff.manage.form.proRatedCost")}
                      </p>
                      <p className="mt-1 text-lg font-bold text-blue-900">
                        {formatMoney(proRatedCost, i18n.language)}
                      </p>
                      <p className="mt-1 text-xs text-blue-600">
                        {t("staff.manage.form.proRatedCostNote")}
                      </p>
                    </div>
                  )}
                {isDoctor && isDoctorLimitReached && !editingStaff && (
                  <div className="p-4 mb-4 bg-red-50 rounded-lg border border-red-200">
                    <p className="text-sm font-medium text-red-800">
                      {currentDoctorCount > maxDoctors
                        ? t("staff.manage.form.doctorOverLicence", {
                            count: currentDoctorCount - maxDoctors,
                            current: currentDoctorCount,
                            max: maxDoctors,
                          })
                        : t("staff.manage.form.doctorLimitReached", {
                            current: currentDoctorCount,
                            max: maxDoctors,
                          })}
                    </p>
                  </div>
                )}
                {isDoctor ? (
                  <DoctorForm
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    control={control as any}
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    setValue={setValue as any}
                    errors={errors}
                    specialties={specialties}
                    selectedSpecialties={selectedSpecialties}
                    editingStaff={editingStaff}
                    isSubmitting={isSubmitting}
                    isSubmitDisabled={
                      !isValid ||
                      !isDirty ||
                      (!editingStaff && isDoctorLimitReached)
                    }
                    onSubmit={handleSubmit(onSubmitForm)}
                    onCancel={onClose}
                    isDoctorLimitReached={isDoctorLimitReached}
                    isSecretaryLimitReached={isSecretaryLimitReached}
                    onUpgradeLicence={onUpgradeLicence}
                  />
                ) : (
                  <SecretaryForm
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    control={control as any}
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    setValue={setValue as any}
                    errors={errors}
                    editingStaff={editingStaff}
                    isSubmitting={isSubmitting}
                    isSubmitDisabled={
                      !isValid ||
                      !isDirty ||
                      (!editingStaff && isSecretaryLimitReached)
                    }
                    onSubmit={handleSubmit(onSubmitForm)}
                    onCancel={onClose}
                    isDoctorLimitReached={isDoctorLimitReached}
                    isSecretaryLimitReached={isSecretaryLimitReached}
                    onUpgradeLicence={onUpgradeLicence}
                  />
                )}
              </>
            )}
    </Modal>
  );
};
